import {
  ApiError,
  ApiPromocodesSchema,
  type ApiPostPromocodeActivate,
} from "@/shared/api"

import defaultNftRewardImageUrl from "./reward/assets/nft.png"
import defaultTonRewardImageUrl from "./reward/assets/ton.png"

const NANOTONS_PER_TON = 1_000_000_000

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set([
  ApiPromocodesSchema.ErrorCode.NotFound,
  ApiPromocodesSchema.ErrorCode.AlreadyUsed,
  ApiPromocodesSchema.ErrorCode.RequirementNotMet,
  ApiPromocodesSchema.ErrorCode.BudgetExceeded,
])

type TranslateFn = (
  path: string,
  params?: Record<string, string | number>
) => string

/** Normalized view consumed by `PromocodeRewardDialog`. */
export interface ClaimedPromoReward {
  amountTon: number
  giftName: string
  imageUrl: string
  lottieUrl: string | null
  wagerRequirement: string | null
}

export function readApiErrorCode(
  err: unknown
): ApiPromocodesSchema.ErrorCode | null {
  if (!(err instanceof ApiError)) return null
  const raw = (err.details as { code?: unknown } | null | undefined)?.code
  return typeof raw === "string" && KNOWN_ERROR_CODES.has(raw)
    ? (raw as ApiPromocodesSchema.ErrorCode)
    : null
}

export function localizeErrorCode(
  code: ApiPromocodesSchema.ErrorCode | null,
  t: TranslateFn
): string {
  switch (code) {
    case ApiPromocodesSchema.ErrorCode.NotFound:
      return t("rewards.promocodeError.notFound")
    case ApiPromocodesSchema.ErrorCode.AlreadyUsed:
      return t("rewards.promocodeError.alreadyUsed")
    case ApiPromocodesSchema.ErrorCode.RequirementNotMet:
      return t("rewards.promocodeError.requirementNotMet")
    case ApiPromocodesSchema.ErrorCode.BudgetExceeded:
      return t("rewards.promocodeError.budgetExceeded")
    default:
      return t("rewards.claimError")
  }
}

export function buildClaimedReward(
  result: ApiPostPromocodeActivate.Response,
  t: TranslateFn
): ClaimedPromoReward | null {
  const items = result.redemption.reward_items
  const headline =
    items.find(
      (item) => item.asset_type === ApiPromocodesSchema.AssetType.Nft
    ) ?? items[0]
  if (!headline) return null

  const tonNano = items.reduce(
    (sum, item) =>
      item.asset_type === ApiPromocodesSchema.AssetType.Ton
        ? sum + (item.amount ?? 0)
        : sum,
    0
  )

  const isNft = headline.asset_type === ApiPromocodesSchema.AssetType.Nft
  const giftName = isNft
    ? typeof headline.nft_id === "string" && headline.nft_id.length > 0
      ? headline.nft_id
      : t("rewards.promocodeRewardName.gift")
    : t("rewards.promocodeRewardName.ton")
  const imageUrl = isNft
    ? typeof headline.large_image_url === "string" &&
      headline.large_image_url.length > 0
      ? headline.large_image_url
      : defaultNftRewardImageUrl
    : defaultTonRewardImageUrl
  const lottieUrl =
    isNft &&
    typeof headline.lottie_animation_url === "string" &&
    headline.lottie_animation_url.length > 0
      ? headline.lottie_animation_url
      : null

  const wagerNano = items.reduce(
    (sum, item) =>
      item.asset_type === ApiPromocodesSchema.AssetType.Ton
        ? sum + (item.wagering_turnover ?? 0)
        : sum,
    0
  )
  const hasNftWager = items.some(
    (item) =>
      item.asset_type === ApiPromocodesSchema.AssetType.Nft &&
      item.wagering_nft_bet === true
  )

  const wagerRequirement =
    wagerNano > 0
      ? t("rewards.promocodeWager.tonTurnover", {
          amount: (wagerNano / NANOTONS_PER_TON).toFixed(2),
        })
      : hasNftWager
        ? t("rewards.promocodeWager.nftBet")
        : null

  return {
    amountTon: tonNano / NANOTONS_PER_TON,
    giftName,
    imageUrl,
    lottieUrl,
    wagerRequirement,
  }
}
